import { useRef } from 'react'
import { useQuery, useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query'
import { Selection } from '@/client/submodules/TransparencyCalculator/types'
import { StorageKey } from '@/client/types'
import { getPrices, updatePrices } from '@/infra/services'

export const useGetPrices = (locale: string = 'es') =>
  useQuery([StorageKey.PRICES, locale], {
    queryFn: () => getPrices(locale),
    enabled: !!locale,
    staleTime: Infinity,
    refetchOnWindowFocus: false
  })

export const useMutatePrices = (locale: string = 'de', freezeCache = false) => {
  const queryClient = useQueryClient()
  // Tracks the latest mutation request to prevent
  // stale responses from overwriting newer responses.
  const latestRequestRef = useRef(0)

  const isLoading = useIsMutating([StorageKey.MUTATE_PRICES])

  const mutation = useMutation({
    mutationKey: [StorageKey.MUTATE_PRICES],

    mutationFn: async (data: Selection) => {
      // Increment request counter for each mutation
      latestRequestRef.current += 1

      const requestId = latestRequestRef.current
      const result = await updatePrices(locale, data)

      return {
        result,
        requestId
      }
    },

    onSuccess: ({ result, requestId }) => {
      if (!freezeCache) {
        //When freeze is enabled, the response will not be cached, so the previous value will remain.
        queryClient.invalidateQueries({ queryKey: [StorageKey.PRICES, locale], refetchType: 'none' })
        // Ignore stale responses.
        // If the response is not from the latest request, ignore it..
        if (requestId !== latestRequestRef.current) {
          return
        }

        queryClient.setQueryData([StorageKey.PRICES, locale], (oldData) =>
          oldData
            ? {
                ...oldData,
                calculatorFields: result.data.calculatorFields,
                optionFields: result.data.optionFields,
                price: result.data.price,
                jobProps: result.data.jobProps
              }
            : oldData
        )
      }
    }
  })

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: !!isLoading
  }
}
